/*
 * Рендерер плашек: тост сверху экрана и модалка подтверждения по центру.
 * Монтируется один раз в App.js (внутри ErrorBoundary, в AppContent).
 * Состояние берёт из модуля toast.js через subscribe.
 * Плашки и модалка рисуются либо в тёплой палитре магазина, либо в slate-палитре
 * админки — тема выбирается по URL (/admin/*), без параметров в API.
 */
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  subscribe, dismissToast, resolveConfirm, resumeToasts,
} from './toast';

// Оформление по видам плашек. Магазин — тёплая палитра (cream/yellow/green),
// админка — slate (белые карточки, обводки #e2e8f0, акцент #D64545/#374151).
const SHOP_TOAST_STYLES = {
  // Сплошной жёлтый: полупрозрачный (старый /60) сливался с кремовым фоном страницы
  info: 'bg-yellow-soft text-green-dark',
  error: 'bg-cream-card text-green-dark border-l-4 border-terracotta',
  success: 'bg-green-primary text-white',
};

const ADMIN_TOAST_STYLES = {
  // Белая карточка на фоне #f1f5f9 с цветным кантом — как карточки админки
  info: 'bg-white text-[#0f172a] border-l-4 border-yellow-soft',
  error: 'bg-white text-[#0f172a] border-l-4 border-[#D64545]',
  success: 'bg-[#0D9488] text-white',
};

export default function ToastHost() {
  const [snap, setSnap] = useState({ toast: null, confirm: null });

  // Тема по URL: админка живёт под /admin, всё остальное — магазин
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith('/admin');
  const toastStyles = isAdmin ? ADMIN_TOAST_STYLES : SHOP_TOAST_STYLES;

  useEffect(() => {
    const unsubscribe = subscribe(setSnap);
    resumeToasts(); // сообщения, показанные до монтажа, теперь оживают с таймером
    return unsubscribe;
  }, []);

  // Esc закрывает модалку подтверждения как отмену («нет»)
  useEffect(() => {
    if (!snap.confirm) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') resolveConfirm(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [snap.confirm]);

  const t = snap.toast;
  const c = snap.confirm;

  return (
    <>
      {t && (
        <div
          key={t.id} // новый id => новый элемент => анимация появления заново
          onClick={dismissToast}
          role="status"
          // top-[68px] — под липкой шапкой (~48px + воздух), иначе плашка перекрывала шапку
          className={`fixed left-1/2 top-[68px] z-50 w-[calc(100%-2rem)] max-w-[420px] -translate-x-1/2 cursor-pointer rounded-2xl p-4 text-sm font-medium leading-snug shadow-sm ring-1 ring-border-warm ${
            toastStyles[t.kind] || toastStyles.info
          } ${
            t.leaving
              ? '-translate-y-2 opacity-0 transition-all duration-200'
              : 'toast-in'
          }`}
        >
          {t.message}
        </div>
      )}

      {c && (
        // Backdrop: затемнение + blur (как в локальных админ-модалках), позиция всегда по центру
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
          onClick={() => resolveConfirm(false)}
        >
          <div
            className={`w-full max-w-sm space-y-4 rounded-2xl p-5 ${
              isAdmin
                ? 'bg-white shadow-2xl' // как модалки админки (например, ProductsAdmin)
                : 'bg-cream-card ring-1 ring-border-warm'
            }`}
            onClick={(e) => e.stopPropagation()} // клик внутри плашки не закрывает
          >
            {/* Вопрос + опциональный detail: detail — мелким серым под основным текстом */}
            <div className="space-y-1">
              <div
                className={`text-sm leading-relaxed ${
                  isAdmin ? 'text-[#0f172a]' : 'text-green-dark'
                }`}
              >
                {c.message}
              </div>
              {c.detail && (
                <div
                  className={`text-xs leading-relaxed ${
                    isAdmin ? 'text-[#64748b]' : 'text-green-muted'
                  }`}
                >
                  {c.detail}
                </div>
              )}
            </div>
            <div className="flex gap-2">
              {/* Primary: обычная тема/опасность × админ/магазин; busy — «...» и обе кнопки disabled */}
              <button
                type="button"
                onClick={() => resolveConfirm(true)}
                disabled={c.busy}
                className={`flex-1 rounded-xl py-2.5 text-sm font-semibold text-white transition active:scale-[0.98] disabled:opacity-50 ${
                  c.danger
                    ? isAdmin
                      ? 'bg-[#D64545] hover:bg-[#D64545]/90' // danger админки
                      : 'bg-terracotta hover:bg-terracotta/90' // danger магазина
                    : isAdmin
                      ? 'bg-[#374151] hover:bg-[#1f2937]'
                      : 'bg-green-primary hover:opacity-90'
                }`}
              >
                {c.busy ? '...' : c.okLabel}
              </button>
              {/* Secondary: одна и та же форма в обеих темах, при busy тоже блокируется */}
              <button
                type="button"
                onClick={() => resolveConfirm(false)}
                disabled={c.busy}
                className={`flex-1 rounded-xl py-2.5 text-sm font-medium transition active:scale-[0.98] disabled:opacity-50 ${
                  isAdmin
                    ? 'border border-[#e2e8f0] bg-white text-[#64748b] hover:bg-[#f8fafc]'
                    : 'border border-border-warm bg-cream text-green-muted hover:opacity-90'
                }`}
              >
                {c.cancelLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
